import React, { useMemo, useEffect, useRef, useContext } from 'react'
import usePrevious from 'hooks/usePrevious'
import { createSelector } from 'reselect'
import { useDispatch, useSelector } from 'react-redux'
import fetchCardsInBoard from 'routes/Board/sagas/fetchCardsInBoard'
import fetchCardFronts from 'routes/Board/sagas/fetchCardFronts'
import fetchCardOverlay from 'routes/Board/sagas/fetchCardOverlay'
import fetchCardRelations from 'routes/Board/sagas/fetchCardRelations'
import useChannel from 'hooks/useChannel'
import { takeEvery } from 'redux-saga/effects'
import { CHANNEL_DATA } from 'CHANNEL_DATA'
import boardChannelData from './boardChannelData'
import useSaga from 'hooks/useSaga'
import fetchView from 'routes/Board/sagas/fetchView'
import { useIdRegister } from '../../hooks/useIdRegister'

function * channelData (handler, args) {
  yield takeEvery(CHANNEL_DATA, handler, args)
}

const BoardChannels: React.FC<{ boardId: number; }> = ({ boardId }) => {
  const incomingMailsDomain = useSelector<any, any>(state => state.kanbo.config.incomingMailDomain)

  useChannel(`board-${boardId}`)
  useChannel(`boardSettings-${boardId}`)
  useChannel(`archive-${boardId}`)
  useChannel(`activity-board-${boardId}`)
  useChannel(incomingMailsDomain ? `incoming-mail-${boardId}` : undefined)

  const args = useMemo(() => ({
    boardId,
  }), [boardId])

  useSaga(channelData, boardChannelData, args)

  return null
}

const DEFAULT_CONTEXT: {
  registerBoard: (boardId: number) => void;
  unregisterBoard: (boardId: number) => void;
  registerCard: (cardId: number) => void;
  unregisterCard: (cardId: number) => void;
} = {
  registerBoard: () => {},
  unregisterBoard: () => {},
  registerCard: () => {},
  unregisterCard: () => {},
}

export const KanBoProviderContext = React.createContext(DEFAULT_CONTEXT)

export function KanBoProvider ({
  children
}) {
  const dispatch = useDispatch()

  const [registeredBoards, registerBoard, unregisterBoard] = useIdRegister<number>(10)
  const [registeredCards, registerCard, unregisterCard] = useIdRegister<number>(0)

  const idsSelector = useMemo(() => createSelector<any, any, any, [number[], number[]]>(
    [
      (state, { registeredBoards }) => registeredBoards,
      (state, { registeredCards }) => registeredCards,
      state => state['view-board'].cards,
    ],
    (
      registeredBoards,
      registeredCards,
      cardsById,
    ) => {
      const boardIdSet = new Set<number>(registeredBoards)
      const cardIdSet = new Set<number>([
        ...registeredCards,
        ...Object.keys(cardsById)
            .filter(key => !cardsById[key].isBusy && registeredBoards.includes(cardsById[key].boardId))
            .map(Number)
      ])

      const cardIds: number[] = Array.from(cardIdSet)
      for (const cardId of cardIds) {
        const card = cardsById[cardId]

        if (card) {
          card.ancestorCardsDates?.forEach(date => { if (!cardsById[date.id]?.isBusy) cardIdSet.add(date.id) })
          card.descendantCardsDates?.forEach(date => { if (!cardsById[date.id]?.isBusy) cardIdSet.add(date.id) })
          card.prevCardDates?.forEach(date => { if (!cardsById[date.id]?.isBusy) cardIdSet.add(date.id) })
          card.nextCardDates?.forEach(date => { if (!cardsById[date.id]?.isBusy) cardIdSet.add(date.id) })
        }
      }

      Array.from(cardIdSet).forEach(cardId => {
        const card = cardsById[cardId]

        if (card?.boardId) {
          boardIdSet.add(card.boardId)
        }
      })

      return [
        Array.from(boardIdSet),
        Array.from(cardIdSet)
      ]
    }
  ), [])
  const [boardIds, cardIds] = useSelector<any, [number[], number[]]>(state => idsSelector(state, {
    registeredBoards,
    registeredCards,
  }))

  const prevBoardIds = usePrevious(boardIds)
  const prevCardIds = usePrevious(cardIds)

  useEffect(() => {
    let addedIds: number[] = []

    if (boardIds.length) {
      if (prevBoardIds?.length) {
        addedIds = boardIds.filter(boardId => !prevBoardIds.includes(boardId))
      } else {
        addedIds = boardIds
      }
    }

    if (addedIds.length) {
      dispatch(addedIds.map(boardId => fetchView({
        boardId,
      })))
      dispatch(addedIds.map(boardId => fetchCardsInBoard({
        boardId,
      })))
    }
  }, [boardIds])

  useEffect(() => {
    let addedIds: number[] = []

    if (cardIds.length) {
      if (prevCardIds?.length) {
        addedIds = cardIds.filter(listId => !prevCardIds.includes(listId))
      } else {
        addedIds = cardIds
      }
    }

    if (addedIds.length) {
      dispatch([
        fetchCardFronts({
          cardIds: addedIds
        }),
        fetchCardOverlay({
          cardIds: addedIds
        }),
        fetchCardRelations({
          cardIds: addedIds
        }),
      ])
    }
  }, [cardIds])

  const value = useMemo(() => ({
    registerBoard,
    unregisterBoard,
    registerCard,
    unregisterCard,
  }), [registerBoard, unregisterBoard, registerCard, unregisterCard])

  return (
    <KanBoProviderContext.Provider value={value}>
      {boardIds.map(boardId =>
        <BoardChannels
          key={boardId}
          boardId={boardId}
        />
      )}
      {children}
    </KanBoProviderContext.Provider>
  )
}

export function useBoardProvider (boardIds: number[]) {
  const prevBoardIds = usePrevious(boardIds)  

  const { registerBoard, unregisterBoard } = useContext(KanBoProviderContext)

  useEffect(() => {
    const addedIds = boardIds.filter(id => !prevBoardIds || !prevBoardIds.includes(id))

    for (const id of addedIds) {
      registerBoard(id)
    }

    if (prevBoardIds) {
      const removedIds = prevBoardIds.filter(id => !boardIds.includes(id))

      for (const id of removedIds) {
        unregisterBoard(id)
      }
    }
  }, [boardIds])

  const onUnmountRef = useRef(() => {})

  useEffect(() => {
    onUnmountRef.current = () => {
      for (const id of boardIds) {
        unregisterBoard(id)
      }
    }
  }, [boardIds, unregisterBoard])

  useEffect(() => {
    return () => {
      onUnmountRef.current()
    }
  }, [])
}

export function useCardProvider (cardIds: number[]) {
  const prevCardIds = usePrevious(cardIds)  

  const { registerCard, unregisterCard } = useContext(KanBoProviderContext)

  useEffect(() => {
    const addedIds = cardIds.filter(id => !prevCardIds || !prevCardIds.includes(id))

    for (const id of addedIds) {
      registerCard(id)
    }

    if (prevCardIds) {
      const removedIds = prevCardIds.filter(id => !cardIds.includes(id))

      for (const id of removedIds) {
        unregisterCard(id)
      }
    }
  }, [cardIds])

  const onUnmountRef = useRef(() => {})

  useEffect(() => {
    onUnmountRef.current = () => {
      for (const id of cardIds) {
        unregisterCard(id)
      }
    }
  }, [cardIds, unregisterCard])

  useEffect(() => {
    return () => {
      onUnmountRef.current()
    }
  }, [])
}